import React from "react";
import * as UI from "@goodgym/components";
import { DatePicker } from "@mui/x-date-pickers";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";


export type AvailabilityFormProps = {
  index: number;
  availability: { date: string; time: string }[];
  setAvailability: (a: { date: string; time: string }[]) => void;
};


export const timeIntervals = Array.from({
  length: ((20 - 8) * 60) / 15 + 1,
}).map((_, i) =>
  dayjs()
    .startOf("day")
    .add(8 * 60 + i * 15, "minute")
    .format("HHmm")
);

export function convertTo12HourFormat(hhmm:string) {
  if (!hhmm || typeof hhmm !== "string" || hhmm.length < 3) return hhmm;
  const h = parseInt(hhmm.slice(0, 2), 10);
  const m = parseInt(hhmm.slice(2), 10);
  if (isNaN(h) || isNaN(m)) return hhmm;

  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const ampm = h < 12 ? "am" : "pm";
  const minStr = m.toString().padStart(2, "0");
  return `${hour12}:${minStr} ${ampm}`;
}
const AvailabilityForm: React.FC<AvailabilityFormProps> = ({
  index,
  availability,
  setAvailability,
}) => {
  const selectedDate = availability[index]?.date
    ? new Date(availability[index].date)
    : null;

  const handleClear = () => {
    const updated = [...availability];
    updated[index] = { date: "", time: "" };
    setAvailability(updated);
  };

  return (
    <UI.Grid columnSpacing={5} container sx={{ mt: 2 }}>
      <UI.Grid item xs={12}>
        <UI.Grid container spacing={3}>
          <UI.Grid item xs={12} md={6}>
            <DatePicker
              label="Date"
              value={selectedDate}
              minDate={new Date()}
              maxDate={
                new Date(new Date().setFullYear(new Date().getFullYear() + 3))
              }
              onChange={(newValue) => {
                let dateIso = "";
                if (newValue instanceof Date && !isNaN(newValue.getTime())) {
                  dateIso = newValue.toISOString();
                } else if (
                  typeof newValue === "object" &&
                  newValue !== null &&
                  typeof (newValue as any).toDate === "function"
                ) {
                  dateIso = (newValue as any).toDate().toISOString();
                } else {
                  dateIso = "";
                }
                const updated = [...availability];
                updated[index].date = dateIso;
                setAvailability(updated);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="medium"
                  sx={{
                    border: "0.1px solid rgba(0, 0, 0, 0.15)",
                    //   boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    mx: "auto",
                    mb: 1,
                    "& .MuiPickersDay-root": {
                      fontSize: "1.15rem",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "primary.main !important",
                      color: "white !important",
                    }
                  }}
                />
              )}
            />
          </UI.Grid>

          <UI.Grid item xs={12} md={6}>
            <UI.FormControl fullWidth>
              <UI.InputLabel id="time-select-label">Time</UI.InputLabel>
              <UI.Select
                id="time-select"
                label="Time"
                onChange={(event) => {
                  const updated = [...availability];
                  updated[index].time = event.target.value;
                  setAvailability(updated);
                }}
                value={availability[index].time}
              >
                {timeIntervals.map((time) => (
                  <UI.MenuItem key={time} value={time} aria-label={time}>
                    {convertTo12HourFormat(time)}
                  </UI.MenuItem>
                ))}
              </UI.Select>
            </UI.FormControl>
          </UI.Grid>
          <UI.Grid item xs={12} md={2}>
            <UI.Button
              variant="outlined"
              color="secondary"
              onClick={handleClear}
              fullWidth
            >
              Clear
            </UI.Button>
          </UI.Grid>
        </UI.Grid>
      </UI.Grid>
    </UI.Grid>
  );
};

export default AvailabilityForm;
